import { useState } from 'react'
import { SelfCheckOutState } from '@/models/self-checkout'
import { GetSelfCheckinSelfCheckinBasicInfoResponse } from '@/models/self-checkin/plugin'
import { checkActivePlugin, PluginCode } from '@/libs/plugins'
import * as api from '@/apis/aipass'
import { cashChangerStorageKey, checkinEposPrinterIdStorage, terminalStorageKey } from '@/constants/self-checkin-device-storage'
import { PaymentMethod, PaymentMethodInput } from '@/models/payment-method'
import { PaymentTerminalSettingType, TerminalPaymentScene } from '@/models/payment-setting'
import { signout } from '@/apis/auth0'
import { useHistory } from 'react-router-dom'
import { DeviceType } from '@/apis/vescajs'

type RequiredOnly<T, U extends keyof T> = T & Required<Pick<T, U>>

export const useSelfCheckOutState = () => {
  const history = useHistory()

  const [state, setState] = useState<SelfCheckOutState>(JSON.parse(sessionStorage.getItem('SelfCheckOutState') || '{}'))

  const saveState = (_state: SelfCheckOutState) => {
    sessionStorage.setItem('SelfCheckOutState', JSON.stringify(_state))
    setState(prevState => ({ ...prevState, ..._state }))
  }

  const clearState = (hotelId: string) => {
    sessionStorage.setItem('SelfCheckOutState', JSON.stringify({ hotelId }))
    // hotelIdだけを引き継ぎ強制クリアを実行
    setState({ hotelId } as any)
  }

  const initializeState = (
    hotelId: string,
    setting: {
      basicInfo: GetSelfCheckinSelfCheckinBasicInfoResponse['results']
      payment: Array<PaymentMethod>
      paymentTerminal: PaymentTerminalSettingType
    },
  ): SelfCheckOutState => {
    const hasPaymentPlugin = checkActivePlugin(setting.basicInfo.plugin, PluginCode.Payment)
    const hasPaymentTerminalPlugin = checkActivePlugin(setting.basicInfo.plugin, PluginCode.PaymentTerminal)
    const storedTerminalIp = localStorage.getItem(terminalStorageKey) || ''
    const storedCashChangerIp = localStorage.getItem(cashChangerStorageKey) || ''
    const isEnabledPaymentTerminal = storedTerminalIp.length > 0 && hasPaymentTerminalPlugin
    const isEnabledCashChanger = storedCashChangerIp.length > 0 && hasPaymentTerminalPlugin
    const payment = hasPaymentPlugin ? setting.payment : []

    const isConnectedTerminal = isEnabledPaymentTerminal || isEnabledCashChanger
    const terminalPaymentScene = setting.paymentTerminal.terminalPaymentScene ?? TerminalPaymentScene.Both

    // チェックアウト時に支払い可能か（端末あり+CheckinOnly以外 or 端末なし+スマート決済設定あり）
    const canPayAtCheckout =
      (isConnectedTerminal && terminalPaymentScene !== TerminalPaymentScene.CheckinOnly) || (!isConnectedTerminal && payment.length > 0)

    return {
      _type: 'selfCheckOut',
      hotelId,
      setting: {
        ...setting.basicInfo,
        basicInfo: setting.basicInfo.basicInfo[0],
        payment,
        paymentTerminal: setting.paymentTerminal,
        isEnabledPaymentTerminal,
        isEnabledCashChanger,
        isEnabledPaymentSetting: canPayAtCheckout,
        isEnabledPrinter: !!localStorage.getItem(checkinEposPrinterIdStorage),
      },
    }
  }

  const doCheckOut = async (_state: SelfCheckOutState, language: string) => {
    api.stateLog(_state)
    const toPaymentMethodInput = (payment: NonNullable<SelfCheckOutState['payment']>): PaymentMethodInput[] => {
      if (payment.kind === 'cash') return [{ method: 'cash' }]
      if (payment.deviceType === DeviceType.PaidQr) return [{ method: 'other', otherName: payment.paymentMethod }]
      return [{ method: 'credit_card' }]
    }
    const res = await api.tabletCheckout({
      hotelId: _state.hotelId,
      language,
      checkinId: _state.selectReservation!.checkinId,
      paymentResult: _state.payment && {
        receiptName: _state.confirm?.receiptName || '',
        orderId: _state.payment.orderId,
        paymentMethod: _state.payment.paymentMethod,
        paymentMethods: toPaymentMethodInput(_state.payment),
      },
      reservations: _state.selectReservation!.selectedReservationAndRooms,
    })
    saveState({
      ..._state,
      complete: res,
    })
  }

  const stateErrorHandling = <K extends keyof SelfCheckOutState>(
    state: SelfCheckOutState | undefined,
    key: K,
  ): state is RequiredOnly<SelfCheckOutState, K> => {
    const hotelIdState = state?.hotelId || sessionStorage.getItem('selfci.hotelId')
    if (!hotelIdState) {
      signout()
      history.replace({ pathname: '/' })
      return false
    }
    if (!state || !state[key]) {
      history.replace({ pathname: '/self-checkin', search: `?hotelId=${hotelIdState}` })
      return false
    }
    return true
  }

  return { state, saveState, clearState, initializeState, doCheckOut, stateErrorHandling }
}
